import { IAssignEntity, BaseUnitOfWorkChangeRequest } from "../basics/basics.models";
import { BaseValidationResponse } from "../validations/validations.models";
import { SubtaskCorrespondenceEmailModel } from "../vetting/vetting.models";
import { DataGridColumn, DataType, ItemSource, QueryFilter } from "../queries/queries.models";
import { HistoryHelper } from "../../helpers/history-helper";
import { TasksConstants } from "../tasks/task.model";
import { MobileHelper } from "../../helpers/mobile-helper";
import { FromNowValueConverter } from "../../views/components/converters/date/date-format";
import * as moment from "moment";
import { EntityAttachmentModel } from "../attachment/attachment.models";
import { Guid } from "../../utilities/guid";
import { IEntityTypeIdentifier } from "@rightship-tech/common-ui/models/basic.models";
import { InspectionStateEnum } from "@rightship-tech/common-ui/models/rightship-inspections/rightship-inspections.models";
import { ThumbnailClientModel } from "../pscs/psc.models";
import { InsightIndicatorModel } from "@rightship-tech/rs-inspections-ui/models/rightship-inspection.models";

export interface RightShipInspectionModel extends IAssignEntity {
    rightShipInspectionId: number;
    vesselId: number;
    incidentId: number;
    pscInspectionId: number;
    notifyShipManager?: boolean;
    vesselOwnerName: string;
    technicalManagmentCompany: string;
    technicalManagerName: string;
    technicalManagerEmail: string;
    correspondenceEmail: string;
    additionalEmailsToNotify: string;
    technicalManagerContactNumber: string;
    technicalManagerContactNumberWithoutCode: string;
    assignedToUserName: string;
    previouslyAssignedToUserName: string;
    agentName: string;
    agentOfficeNumber: string;
    agentOfficeNumberWithoutCode: string;
    inspectorContactNumber: string;
    inspectorEmail: string;
    agentEmail: string;
    berth: string;
    inspectionRequestComments: string;
    requiresRightshipInspectionComments: string;
    assignmentComment: string;
    hasVet: boolean;
    numberOfDeficiencies?: number;
    currentRightShipInspectionStatusHistoryId?: number;
    countryId?: number;
    portId?: number;
    cargoOperationId?: number;
    businessUnitId?: number;
    previouslyAssignedTo?: number;
    inspectingCompanyId?: number;
    assignedTo?: number;
    reasonForInspectionId?: number;
    inspectorId?: number;
    overallAssessmentId?: number;
    rightShipInspectionTypeId?: number;
    useMobileInspectionWorkflow: boolean;
    entityType?: number;
    isVesselOwnerInformedAndAgreed?: boolean;
    antiBriberyAndCorruptionPolicy?: boolean;
    noLetterOfIndemnityWillBeSigned?: boolean;
    agreeToSafetyScoreClause?: boolean;
    ackWatchedMessageCentreVideo?: boolean;
    bothOwnerAndManagerPromptlyReport?: boolean;
    agreeToTermsAndConditions?: boolean;
    agreeToTermsAndConditionsDateTime?: Date;
    allDeficienciesReviewed?: boolean;
    fullInspectionReportReviewed?: boolean;
    lengthOfTimeAtBerth?: number;
    estimatedArrivalDate?: Date;
    assignedDate?: Date;
    validityExpiryDate?: Date;
    requestedDateOfInspection?: Date;
    actualDateOfInspection?: Date;
    rightShipInspectionDeficiencies?: RightShipInspectionDeficiencyModel[];
    invoices?: RightShipInspectionInvoiceModel[];
    cargillInspectionDetails?: CargillInspectionDetailModel;
    wetInspectionDetails?: WetInspectionDetailModel;
    currentRightShipInspectionStatusHistory?: RightShipInspectionStatusModel;
    rightShipInspectionVesselTypeId?: number;
    isSelfUnloadingVessel?: boolean;
    isUsingLngFuel?: boolean;
    isEquippedWithGantryCranes?: boolean;
    hasIceClassNotation?: boolean;
    isGearlessVessel?: boolean;
    isRoRoShip?: boolean;
    isTimberCarrier?: boolean;
    isCarryingGrainCargoAndHasBeenFumigatedInThePast12Months?: boolean;
    isHybrid: boolean;
    tabletSyncStatusId?: number;
    tabletSyncStatus?: string;
    tabletLastSyncDate?: Date;
    crewMatrixFormSubmitDate?: Date;
    cargoOperationComments?: string;
    captainName: string;
    captainEmail: string;
    inspectionSourceId?: number;
    entityStatusId?: number;
    isVesselInvolvedInSts?: boolean;
}

export interface WetInspectionDetailModel {
    legacyWetInspectionVesselOperationId?: number;
    legacySpmMooringSuitable?: boolean;
    legacyStsSuitable?: boolean;
    legacyHullTypeId?: number;
    legacyTradingAreaId?: number;
    legacyWetInspectionVrsSuitabilityId?: number;
    wetInspectionTypeId: number;
}

export interface CargillInspectionDetailModel {
    shipSuitable?: boolean;
    accommodationSeverity?: number;
    accommodationProbability?: number;
    cargoAndBallastSystemsSeverity?: number;
    cargoAndBallastSystemsProbability?: number;
    hullAndDeckItemsSeverity?: number;
    hullAndDeckItemsProbability?: number;
    machinerySpacesSeverity?: number;
    machinerySpacesProbability?: number;
    pollutionManagementSeverity?: number;
    pollutionManagementProbability?: number;
    safetyManagementSeverity?: number;
    safetyManagementProbability?: number;
    navigationSeverity?: number;
    navigationProbability?: number;
    crewManagementSeverity?: number;
    crewManagementProbability?: number;
    certificationAndDocumentationSeverity?: number;
    certificationAndDocumentationProbability?: number;

}

export interface RightShipInspectionStatusModel {
    rightShipInspectionStatusId: number;
}

export interface RightShipInspectionInvoiceModel {
    rightShipInspectionInvoiceId?: number;
    rightShipInspectionId?: number;
    billingCustomerId?: number;
    invoiceStatusId?: number;
    contactName?: string;
    companyName?: string;
    address?: string;
    city?: string;
    state?: string;
    countryId?: number;
    country?: string;
    postCode?: string;
    billingInstructions?: string;
    phoneNumber?: string;
    phoneNumberWithoutCode?: string;
    phoneNumberCountryCode?: string;
    email?: string;
    additionalEmailsToNotify?: string;
    invoiceNumber?: string;
    billingCustomer?: string;
    billingCustomerCode?: string;
    supplierInvoiceCurrency?: string;
    supplierInspectionAmount?: number;
    supplierTravelAndExpenseAmount?: number;
    legacySupplierInvoiceTotal?: number;
    salesCurrency?: string;
    salesAmount?: number;
    invoiceStatus?: string;
    inspectionMarkup?: number;
    invoiceReceivedDate?: Date;
    purchaseOrderNo?: string;
    billingAccountId?: number;
    billingAccount?: string;
    cancelledInspectionPenalty?: number;
    lateInspectionPenalty?: number;
    otherPenaltyAmount?: number;
    totalSupplierInvoiceAmount?: number;
    currencyConversionRateId?: number;
    currencyConversionRate?: number;
    supplierTravelAndExpenseAmountUSD?: number;
    totalSupplierInvoiceAmountUSD?: number;
    generatedInvoiceNumber?: string;
}

export interface RightShipInspectionDeficiencyModel {
    rightShipInspectionDeficiencyId?: number;
    rightShipInspectionId?: number;
    vetSuperOutcomeModifiedBy?: number;
    vetSuperOutcomeId?: number;
    deficiencyCategoryId?: number;
    deficiencyId?: number;
    createdDate?: Date;
    lastModifiedDate?: Date;
    vetSuperOutcomeLastModifiedDate?: Date;
    vetSuperModifiedOutcome?: boolean;
    likelyToOccurInVesselAgain?: boolean;
    likelyToOccurWithinFleet?: boolean;
    code?: string;
    comment?: string;
}

export interface RightShipInspectionClientModel extends IEntityTypeIdentifier {
    questionNumber?: string;
    notifyShipManagerOnCloseOut?: boolean;
    allDeficienciesReviewed?: boolean;
    fullInspectionReportReviewed?: boolean;
    vetTaskId?: number;
    vetId?: number;
    overallAssessmentId?: number;
    inspectingCompany?: string;
    inspectingCompanyEmail?: string;
    inspectorId?: number;
    inspector2Id?: number;
    inspector?: string;
    inspector2?: string;
    reasonForInspection?: string;
    reasonForInspectionId?: number;
    inspectingCompanyId?: number;
    validityExpiryDate?: Date;
    emails?: SubtaskCorrespondenceEmailModel[];
    rightShipInspectionId: number;
    id: number;
    vesselId: number;
    vesselDwt?: number;
    inspectionTypeId?: number;
    vesselType?: string;
    additionalEmailsToNotify?: string;
    vesselName?: string;
    vesselFlag?: string;
    inspectionRequestComments?: string;
    requiresRightshipInspectionComments?: string;
    rightShipInspectionStatus?: string;
    vesselImo?: string;
    vehicleIdentificationNumber?: string;
    europeanNumberOfIdentification?: string;
    port?: string;
    portId?: number;
    requestedDateOfInspection?: Date;
    actualDateOfInspection?: Date;
    customerId?: number;
    billingCustomer?: Date;
    country?: string;
    countryId?: number;
    vesselDateOfBuild?: Date;
    estimatedArrivalDate?: Date;
    assignedTo?: string;
    createdBy?: string;
    rating?: string;
    assignedUserId?: number;
    safetyRatingType?: number;
    starRating?: number;
    ratingPlus?: boolean;
    rightShipInspectionStatusId?: number;
    deficiencies?: RightShipInspectionDeficiencyClientModel[];
    statuses?: RightShipInspectionStatusClientModel[];
    reportData: { [index: number]: RightShipInspectionReportClientModel[] };
    cargillInspectionDetails: CargillInspectionDetailModel;
    lengthOfTimeAtBerth?: number;
    lengthOfTimeAtBerthDisplayText?: string;
    reason?: string;
    invoice: RightShipInspectionInvoiceModel;
    documentsCount?: number;
    privateNotesCount?: number;
    correspondenceCount?: number;
    previousValidityExpiryDate?: string;
    rootCauseAnalysisResponseRatingId: number;
    correctiveActionResponseRatingId: number;
    preventiveActionResponseRatingId: number;
    isHybrid?: boolean;
    agentName?: string;
    agentEmail?: string;
    agentOfficeNumber?: string;
    correspondenceEmail?: string;
    technicalManagerEmail?: string;
    inspectorEmail?: string;
    inspector2Email?: string;
    inspectorContactNumber?: string;
    inspector2ContactNumber?: string;
    canForceTabletSync?: boolean;
    useMobileInspectionWorkflow?: boolean;
    cargoOperation?: string;
    formHasBeenSubmitted?: boolean;
    cargoOperationComments?: string;
    rightShipInspectionGradeId?: number;
    rightShipInspectionGrade?: string;
    rightShipInspectionGradeCode?: string;
    rightShipInspectionGradeToolTipText?: string;
    hasSurveyResponse?: boolean;
    tabletSyncStatus?: string;
    validityDateChangedByUserName?: string;
    validityDateChangedOnDate?: Date;
    reviewer?: string;
    hasIncompleteFinding?: boolean;
    hasAssignedInspector?: boolean;
    billingStatusId?: number;
    billingStatus?: string;
    inspectionInvoiceNumberPrefix?: string;
    hasWithdrawnProposal?: boolean;
    hasInspectorUnassigned?: boolean;
    lastRequestedDateOfInspection?: Date;
    hasChangedInspectionDate?: boolean;
    reasonForDurationOfValidityId?: number;
    summary: string;
    reasonForDurationOfValidity: string;
    additionalCommentsForDurationOfValidity?: string;
    createdDate?: Date;
    hasReceivedFinalReport?: boolean;
    pscDate?: Date;
    pscPlace?: string;
    incidentDate?: Date;
    hasFirstReviewDone: boolean,
    firstReviewComments: string,
    firstReviewedBy: string,
    canSendInvoiceEmail: boolean;
    isShowStopper: boolean;
    regulatoryHighRiskInsightSummary: string;
    standardHighRiskInsightSummary: string;
    regulatoryMediumRiskInsightSummary: string;
    standardMediumRiskInsightSummary: string;
    regulatoryLowRiskInsightSummary: string;
    standardLowRiskInsightSummary: string;
    hasInspectionOutcomeV2: boolean;
    closeoutTypeId?: number,
    closeoutType: string,
}

export interface RightShipInspectionStatusClientModel {
    name: string;
    statusId: number;
    subtaskTypes?: number[];
    comment?: string;
    fullComment?: string;
    user: string;
    date: Date;
    previousStatusId?: number;
    inspectorName?: string; 
}

export interface RightShipInspectionInsightModel {
    rightShipInspectionId: string;
    description: string;
    comments: string[];
    findings: string[];
    inspectionDate?: string;
    causeOfConcern: string;
    grading: string;
}
export interface RightShipInspectionDeficiencyClientModel {
    rightShipInspectionDeficiencyId: number;
    deficiencyRiskAreaId?: number;
    deficiencyCategoryId?: number;
    deficiencyId?: number;
    deficiency: string;
    code: string;
    group: string;
    likelyToOccurInVessel: string;
    likelyToOccurInFleet: string;
    comment: string;
    reason: string;
    createdDate?: Date;
    lastModifiedDate?: Date;
    vetSuperOutcomeLastModifiedDate?: Date;
    vetSuperOutcomeModifiedBy?: number;
    vetSuperOutcomeId?: number;
    vetSuperModifiedOutcome?: boolean;
    manualDefectText?: string;
}

export interface RightShipInspectionReportClientModel {
    index: number;
    rightShipInspectionReportId: number;
    value: string;
    bookmark: string;
    title: string;
}

export interface SaveRightShipInspectionRequest extends BaseUnitOfWorkChangeRequest {
    correspondenceMessage?: string;
    isRequest?: boolean;
    manualPort?: boolean;
    port?: string;
    inspectionSourceId?: number;
    countryId?: number;
    inspectionTypeId?: number;
    attachments?: File[];
    sendEmail?: boolean;
    notifyShipManager?: boolean;
    skipValidation?: boolean;
    useMobileInspectionWorkflow?: boolean;
}

export interface SaveRightShipInspectionBillingDetailsRequest extends BaseUnitOfWorkChangeRequest {
    submitOrderToSalesforce?: boolean;
    shouldNotifyInspectors?: boolean;
}

export interface RestoreFindingToAIContentRequest {
    rightShipInspectionDeficiencyId: number;
    aiRunId: string;
}

export interface SaveFindingGradingRequest extends BaseUnitOfWorkChangeRequest {
    findingCausations?: any[]
}

export interface ChangeRightShipInspectionValidityDateRequest {
    validityExpiryDate: Date;
    rightShipInspectionId: number;
    comments: string;
    requiresNewInspection: boolean;
    notifyShipManager: boolean;
    shipManagerEmails: SubtaskCorrespondenceEmailModel[];
    correspondenceToShipManager: string;
    changeValidityDateReasonId: number;
}

export interface ChangeInspectionCompanyRequest {
    rightShipInspectionId: number;
    inspectionDate?: Date;
    inspectionCompanyId?: number;
    countryId?: number;
    portId?: number;
    lengthOfTimeAtBerth?: number;
    isHybrid?: boolean;
    notifyRelevantParties?: boolean;
    emails?: {
        emailRecipientTypeId?: number;
        email: string;
        isInspectionCompanyEmail?: boolean;
    }[];
    emailMessage?: string;
}

export interface GetCrewMatricesByInspectionIdRequest {
    rightShipInspectionId: number;
}

export interface ChangeRSInspectionInspectorRequest {
    rightShipInspectionId: number;
    inspectorId?: number;
    inspectorEmail?: string;
    inspectorContactNumber?: string;
    inspector2Id?: number;
    inspector2Email?: string;
    inspector2ContactNumber?: string;
}

export interface SendInspectionEmailRequest {
    email: string;
    portName: string;
    country: string;
    inspectionId: number;
    vesselsName: string;
    iMO: string;
    flag: string;
    vesselsManager: string;
    inspectionDate: Date;
    lastUpdateByVesselsManager: Date;
    companyName?: string;
}

export interface AssignInspectorAssignmentRequest {
    inspectionId: number;
    inspectorAssignmentId: number;
    inspectorAssignmentFormId: number;
}

export interface SaveRightShipInspectionResponse extends BaseValidationResponse {
    linkedVetIds?: number[];
    rightShipInspectionStatusId?: number;
    vesselId?: number;
}

export interface ChangeRightShipInspectionValidityDateResponse extends BaseValidationResponse {
    linkedVetIds?: number[];
    rightShipInspectionStatusId?: number;
    vesselId?: number;
}

export interface UpdateTaskOutcomeResponse extends BaseValidationResponse {
    vetHasCustomerDecision: boolean
}

export interface GetRequiredRightShipInspectionResponse {
    rightShipInspection: RightShipInspectionModel;
    rightShipInspectionStatusId: number;
    hasPendingInspection: boolean;
}


export interface RightShipInspectionExecutiveSummaryResponse {
    inspectorReportResponse: GetInspectorReportResponse;
    nonConformitiesTrendlineData: RightShipInspectionNonConformitiesTrendlineDataModel[];
    nonConformitiesByRiskLevel: RightShipInspectionNonConformitiesByRiskLevelModel[];
    personnelTimeInRank: RightShipInspectionPersonnelTimeInRankModel[];
}

export interface RightShipInspectionNonConformitiesTrendlineDataModel {
    rightShipInspectionId: number;
    inspectionDate: Date;
    noOfNonConformities: number;
}

export interface RightShipInspectionPersonnelTimeInRankModel {
    rightShipInspectionId: number;
    rankGroup: number;
    totalTimeInRank: number;
}

export interface RightShipInspectionNonConformitiesByRiskLevelModel {
    rightShipInspectionId: number;
    riskLevelName: string;
    noOfNonConformities: number;
}

export interface GetInspectorReportResponse {
    rightShipInspectionId: number;
    sections?: InspectorReportSectionModel[];
    filterCounts?: FilterCountsModel;
    isFiltered?: boolean;
    hasRecievedFinalReport?: boolean;
}

export interface GetCrewMatricesByInspectionIdResponse {
    crewMatrixComment: string;
    items: CrewMatrixItem[];
}

export interface CrewMatrixItem {
    rightShipInspectionCrewMatrixId: number;
    rank: string;
    gender: string;
    officerType: string;
    nationalityCountry: string;
    certificateOfCompetency: string;
    issuingCountry: string;
    endorsementByVesselFlag: string;
    radioQualification?: boolean;
    vesselsManager?: number;
    managerRank?: number;
    dryCargoShip?: number;
    yearsWatchKeeping?: number;
    englishProficiency: string;
    dateOfJoining: Date;
    dateOfJoiningFormatted: string;
    monthsOnBoard?: number;
}

export interface InspectorReportSectionModel {
    rightshipInspectionFormSectionId: number,
    sectionNumber?: string;
    section: string;
    questions?: InspectorReportQuestionModel[];
}

export interface InspectorReportQuestionModel {
    questionNumber?: string;
    question: string;
    outcome: string;
    comments?: string;
    rightshipInspectionAnswerClassificationId?: number,
    rightshipInspectionFormSubmissionAnswerId?: number,
    isOverriden?: boolean,
    attachmnents?: EntityAttachmentModel[];
    rightShipInspectionDeficiency?: InspectorReportQuestionDeficiencyModel;
    lastModifiedDate?: Date;
    createdOrUpdatedBy?: string;
}

export interface InspectorReportQuestionDeficiencyModel {
    rightShipInspectionDeficiencyId: number;
    rightShipInspectionId?: number;
    manualDefectText?: string;
    comment?: string;
    isActive?: boolean;
    vetSuperModifiedOutcome?: boolean;
}

export enum RightshipInspectionAnswerClassificationsEnum {
    Yes = 1,
    No = 2,
    Na = 3,
    Nv = 4
}
export interface FilterCountsModel {
    yesCount?: number;
    yesWithCommentsCount?: number;
    noCount?: number;
    naCount?: number;
    nvCount?: number;
}

export interface GetRSInspectionNonConformityResponse {
    sectionNonConformityCounts: NonConformitySectionCountsModel;
    nonConformityRFI: RSInspetionNonConformityRFIModel;
    nonConformities: RightShipInspectionNonConformityModel[];
    nonConformityCloseoutSubtaskType?: number;
}

export interface RestoreFindingToAIContentReponse {
    causation: FindingCausationNonConformityModel;
}

export interface GetRSInspectionOutcomeResponse {
    regulatory: number[];
    regulatoryCount: number;
    industryStandard: number[];
    industryStandardCount: number;
    highRiskCount: number;
    mediumRiskCount: number;
    lowRiskCount: number;
}

export interface RSInspetionNonConformityRFIModel {
    subtaskId: number;
    subtaskStatusId?: number;
    subtaskStatus?: string;
    createdDate?: Date;
    statusDate?: Date;
    subtaskName?: string;
}

export interface NonConformitySectionCountsModel {
    [section: string]: { count: number, isHidden: boolean }
}

export interface RightShipInspectionNonConformityModel {
    id?: number;
    rightShipInspectionDeficiencyId?: number;
    questionNumber?: string;
    sectionId?: number;
    sectionNumber?: string;
    sectionName?: string;
    sectionSequence: number;
    questionText?: string;
    comment?: string;
    nonConformityClassificationId?: number;
    nonConformityClassificationName?: string;
    nonConformityCategoryId?: number;
    nonConformitySubCategoryId?: number;
    riskLevelClassificationId?: number;
    isBestPractice?: boolean;
    createdDate?: Date;
    lastModifiedDate?: Date;
    vetSuperOutcomeLastModifiedDate?: Date;
    vetSuperOutcomeModifiedBy?: number;
    vetSuperOutcomeId?: number;
    vetSuperModifiedOutcome?: boolean;
    rightshipInspectionFormSubmissionAnswerId?: number;
    nonConformityDescription?: string;
    attachments?: EntityAttachmentModel[];
    riskLevelId?: number;
    riskClassificationId?: number;
    isGradingConfirmed?: boolean;
    isSaveForLater?: boolean;
    peopleClassification?: boolean;
    processClassification?: boolean;
    plantClassification?: boolean;
    findingClassifications: string;
    defaultRiskLevelId?: number;
    defaultRiskClassificationId?: number;
    currentFindingCausations?: FindingCausationNonConformityModel[];
    findingCausations?: FindingCausationNonConformityModel[];
    aiExtraction: InspectionCosmosDeficiencyExtractionModel;
    hasAiOverriden: boolean;
    element?: HTMLElement;
    isAiCausationOverriden?: boolean;
    rootCause?: string;
    preventActions?: string;
    correctiveActions?: string;
    ownerResponse?: string;
    repeatedFinding?: boolean;
    showStopper?: boolean;
    classificationSourceModels: FindingCausationClassificationSourceModel[];
    thumbnails?: string;
    aiRunId?: string;
}

export interface FindingCausationNonConformityModel {
    rightShipInspectionFindingCausationId?: number;
    findingCausationL1Id?: number;
    findingCausationL2Id?: number;
    findingCausationL3Id?: number;
    findingCausationL4Id?: number;
    findingCausationL5Id?: number;
    findingCausationL6Id?: number;
    findingCauseOfConcernId?: number;
    rightShipInspectionDeficiencyId?: number;
    isActive: boolean;
    aiRunId?: string;
}

export interface FindingCausationClassificationSourceModel {
    rightshipInspectionId: string;
    rightShipInspectionDeficiencyId: string;
    questionText: string;
    actualFindings: string;
    actualCausation: string;
    actualCauseofConcern: string;
    inspectionDate: string;
    riskLevelId?: number;
    riskClassificationId?: number;
    inspectorComment: string;
    questionRiskGrading: string;
}

export interface InspectionCosmosDeficiencyExtractionModel {
    thumbnails: InspectionThumbnail[];
    aiRunId: string;
    rootCause: string;
    preventativeAction: string;
    correctiveAction: string;
    ownerResponse: string;
    isCloseoutOverriden?: boolean;
}

export interface GetInspectorReportRequest {
    rightShipInspectionId: number;
    searchFilter?: string;
    yesFilter?: boolean;
    yesWithCommmentsFilter?: boolean;
    noFilter?: boolean;
    naFilter?: boolean;
    nvFilter?: boolean;
}

export interface ChangeInspectionWorkflowRequest {
    rightShipInspectionId: number;
    useMobileWorkflow: boolean;
}

export enum RightshipInspectionBillingStatusEnum {
    MissingBillingDetails = 1,
    BillingDetailsSet = 2,
    SupplierInvoiceReceived = 3,
    SalesOrderCreated = 4,
    SalesOrderInitiated = 5
}

export enum RightShipInspectionType {
    RightShipInspection = 1,
    CargillSurvey = 2,
    TankerInspection = 3
}

export enum RightShipInspectionReportEnum {
    PreliminaryReport = 1,
    FinalReport = 2,
    DeficienciesReport = 3
}

export enum RightShipInspectionSourceEnum {
    Website = 1,
    Platform = 2,
    WebsiteResubmission = 3
}

export enum RightShipInspectionReasonEnum {
    AgeRequirement = 1,
    Abandonment_WelfareConcerns = 2,
    Other = 3,
    ProactiveRequest = 4,
    UnsatisfactoryPSCPerformanceOverTime = 5,
    UnsatisfactoryIncidentPerformanceOverTime = 6,
    UnsatisfactoryPSCCloseout = 7,
    SevereDetention = 8,
    UnsatisfactoryIncidentInvestigation = 9,
    VettingRequirement = 10,
    ReinspectionAfterUnsatisfactoryRightShipInspection = 11
}

export interface RightShipInspectionFindingsInsights {
    name: string;
    classifications: RightShipInspectionClassification[];
}

export interface RightShipInspectionClassification {
    name: string;
    description: string;
}

export enum RightShipInspectionStatusEnum {
    InspectionRequired = 1,
    NewInspection = 2,
    InProgress = 3,
    Scheduled = 4,
    AwaitingResponse = 5,
    ReviewRequired = 6,
    Acceptable = 7,
    RightShipInspectionReassignment = 8,
    Unacceptable = 9,
    Cancelled = 10,
    InspectorAssigned = 11,
    Booked = 12,
    AlternativeRequired = 13,
    FirstReviewDone = 14,
    Invalid = 101,
    Incomplete = 102
}

export enum RightShipInspectionCancellationEntityEnum {
    Invalid = "RightShip has invalidated the inspection",
    Incomplete = "RightShip has marked the inspection as incomplete"
}

export interface IInspectionStateEntity {
    inspectionState?: InspectionStateEnum;
}

export interface RSInspectionCompanyResponseModel {
    rightShipInspectionId: number;
    inspectionCompany?: string;
    inspectionCompanyEmail?: string;
    vesselName?: string;
    vesselIMO?: string;
    vesselVIN?: string;
    vesselENI?: string;
    port?: string;
    country?: string;
    inspectionType?: string;
    inspectionDate: Date;
    technicalManager?: string;
    technicalManagerEmail?: string;
    rightShipInspectionEmail?: string;
    agentName?: string;
    agentOfficeNumber?: string;
    agentEmail?: string;
    inspectionGuid?: Guid;
    inspectionCompanyResponse?: boolean;
    inspectionCompanyCCEmail?: string;
    additionalEmail?: string;
    correspondenceEmail?: string;
}

export interface SubmitRSInspectionCompanyResponse {
    inspection: RSInspectionCompanyResponseModel;
    message?: string;
    isSuccessful?: boolean;
    alreadySubmitted?: boolean;
}

export interface SubmitRSInspectionCompanyRequest {
    guid: Guid;
    response: boolean;
    rejectedReason?: string;
    furtherInformation?: string;
}

export interface InviteInspectorRequest {
    inspectionId: number;
    companyIds: number[];
    freelancerIds: number[];
}

export interface ResendBookingRequestRequest {
    bookingRequestId: number
}

export interface InviteInspectorResponse {
    ids: number[];
    isSuccess: boolean;
    message: string;
    validationResult: any;
}

export interface ResendAcceptanceEmailsRequest {
    rightShipInspectionId: number;
    rightShipInspectionInspectorAssignmentId: number;
    emailsToSend: AcceptanceEmailModel[]
}

export interface ResendInvoiceEmailRequest {
    rightShipInspectionId: number;
    country: string;
    portName: string;
    vesselImo: string;
    vesselName: string;
    inspectionDate: Date;
    inspectingCompany: string;
    emailsToSend: AcceptanceEmailModel[]
}

export interface AcceptanceEmailModel {
    email: string;
    inspectorType: number;
}

export class RightshipInspectionActivitiesColumns {

    public static getColumns(openInANewTab: boolean, fromVet: boolean, rightShipUser: boolean = false, permissions: { showSurvey: boolean }) {
        const columns: DataGridColumn[] = [];
        const newTab = openInANewTab ? 'target="_blank"' : '';
        columns.push(
            <DataGridColumn>{
                columnOrder: 1,
                sortOrder: 2,
                dataType: DataType.CustomTemplate,
                name: 'vesselName',
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Name",
                value: '<div class="column">' +
                    '<div class="row align-items-middle">' +
                    '<a class="link" href="/insp/${item.rightShipInspectionId}?source=activities" ' + newTab + ' mousedown.trigger="itemClick()">${item.vesselName}</a>' +
                    '<svg class="rs-badge" viewBox="0 0 30 30" if.bind="item.isRightShipMember && isRightShipUser">' +
                    '<g>' +
                    '<path fill="#003745" d="M2,0h26c1.1,0,2,0.9,2,2v26c0,1.1-0.9,2-2,2H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '<path fill="#FF5F1B" d="M2,0h1.5c0.5,0,1,0.2,1.4,0.6l15.5,14.8l0,0L4.9,29.5C4.5,29.8,4,30,3.5,30H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '</g>' +
                    '</svg>' +
                    '</div>' +
                    '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span></div>',
                instancePerRow: true,
                isSortable: true,
                parameter: {
                    item: null,
                    isRightShipUser: rightShipUser,
                    itemClick: () => {
                        HistoryHelper.addState(TasksConstants.historyTaskTabKey, TasksConstants.INSPListName);

                        return true;
                    }
                }
            },
            <DataGridColumn>{
                columnOrder: 2,
                sortOrder: 8,
                dataType: DataType.CustomTemplate,
                name: 'submittedDate',
                title: 'Submitted Date',
                isSortable: true,
                secondParameter: 'MMMM Do YYYY',
                value: '<div class="column">' +
                    '<span>${getDateToString(item.submittedDate, false)}</span>',
                parameter: {
                    item: null,
                    getDateToString: (date: Date | string, showDateFromNow: boolean) => this.getDateToString(date, showDateFromNow)
                },
                instancePerRow: true

            },
            <DataGridColumn>{
                columnOrder: 2,
                sortOrder: 1,
                dataType: DataType.Default,
                name: 'rightShipInspectionType',
                title: 'Type',
                isSortable: true,
            },
            <DataGridColumn>{
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.Default,
                isSortable: true,
                name: 'portName',
                title: 'Port',
                valueConverter: (value) => value == null ? 'Unknown Port' : value
            },
            <DataGridColumn>{
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                name: 'inspectionDate',
                title: 'Inspection Date',
                secondParameter: 'MMMM Do YYYY',
                value: '<div class="column">' +
                    '<span>${getDateToString(item.inspectionDate, false)}</span>',
                parameter: {
                    item: null,
                    getDateToString: (date: Date | string, showDateFromNow: boolean) => this.getDateToString(date, showDateFromNow)
                }
            },
            <DataGridColumn>{
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'customerName',
                title: 'Customer/Inspection ID',
                value:
                    '<div class="reviewer" if.bind="item.businessUnitName">' +
                    '<span class="name">${item.customerName} - ${item.businessUnitName}' +
                    '</span>' +
                    '</span>' +
                    '<span class="type" if.bind="item.rightShipInspectionId">Inspection ID #${item.rightShipInspectionId}</span>' +
                    '</div>' +
                    '<div class="reviewer" if.bind="!item.businessUnitName">' +
                    '<span class="type" if.bind="item.rightShipInspectionId">RightShip IT / Inspection ID #${item.rightShipInspectionId}</span>' +
                    '</div>',
                isSortable: true,
                parameter: {
                    item: null,
                }
            });

        if (!fromVet)
            columns.push(<DataGridColumn>{
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                name: 'inspector',
                title: 'Inspector',
                value:
                    '<div class="reviewer">' +
                    '<span class="name" if.bind="item.inspector">${item.inspector}</span>' +
                    '<span class="type" if.bind="item.inspectingCompany">${item.inspectingCompany}</span>' +
                    '</div>',
                parameter: {
                    item: null,
                }
            });

        columns.push(
            <DataGridColumn>{
                columnOrder: 7,
                sortOrder: 7,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                name: 'reviewer',
                title: 'Reviewer',
                value:
                    '<div class="reviewer" if.bind="item.reviewer && item.statusId > 2">' +
                    '<span class="name">${item.reviewer}</span>' +
                    '<span class="type" if.bind="item.reviewerType">${item.reviewerType}</span>' +
                    '</div>',
                parameter: {
                    item: null,
                }
            },
            <DataGridColumn>{
                columnOrder: 8,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                name: 'status',
                title: 'Status',
                value:
                    '<div class="status-badge-value" if.bind="item.status">' +
                    '<div class="row nowrap align-items-middle">' +
                    '<div class="status-badge-dot"></div>' +
                    '<span>${item.status}</span>' +
                    '<div class="info" if.bind="item.awaitingResponseStatusReason && item.status.toLowerCase() != \'unacceptable\' && item.status.toLowerCase() != \'acceptable\'">' +
                    '<i class="icon-info material-icons">info_outline</i>' +

                    //Tooltip
                    '<div class="status-tooltip">' +
                    '<div class="wrap-tooltip">' +
                    '<p class="desc">${getReasonText(item)}</p>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '<span class="status-date">${getDateToString(item.statusDate, true)}</span>' +
                    '</div>',
                getCellClass: (item: { status: string }) => {
                    return (item && item.status) ? `status-badge ${item.status.toLowerCase().replace(/[^A-z0-9]+/g, '-')}` : '';
                },
                parameter: {
                    item: null,
                    getDateToString: (date: Date | string, showDateFromNow: boolean) => this.getDateToString(date, showDateFromNow),
                    getReasonText: (item) => {                        
                        if (item.statusId == RightShipInspectionStatusEnum.Incomplete ||
                            item.statusId == RightShipInspectionStatusEnum.Invalid ||
                            item.statusId == RightShipInspectionStatusEnum.Cancelled)
                            return 'Reason: ' + item.awaitingResponseStatusReason;
                        else
                            return item.awaitingResponseStatusReason;
                    }
                }
            },
            <DataGridColumn>{
                columnOrder: 8,
                sortOrder: 8,
                dataType: DataType.Default,
                instancePerRow: true,
                isSortable: true,
                name: 'billingStatus',
                title: 'Billing Status'
            },
            <DataGridColumn>{
                columnOrder: 9,
                sortOrder: 9,
                dataType: DataType.Number,
                instancePerRow: true,
                isSortable: true,
                name: 'unreadMessage',
                title: 'Unread Messages'
            });

        if (rightShipUser && permissions.showSurvey)
            columns.push(
                <DataGridColumn>{
                    columnOrder: 10,
                    sortOrder: 1,
                    dataType: DataType.Default,
                    name: 'additionalInfo',
                    title: 'Additional Info',
                    isSortable: true,
                    width: '160px'
                });
        return columns;
    }

    public static getDateToString(date: Date | string, showDateFromNow: boolean, dateFormat: string = null): string {
        if (!date)
            return ''

        let val: Date;

        if (typeof date == 'string')
            val = new Date(date);
        else
            val = date

        if (val.getUTCMonth() == 0 && val.getUTCDay() == 1 && val.getUTCFullYear() == 1900)
            return 'N/A';
        if (showDateFromNow) {
            return new FromNowValueConverter().toView(val);
        }
        else {
            return moment(val).format(dateFormat ? dateFormat : 'DD MMM YYYY HH:mm');
        }
    }
}

export interface RightShipInspectionODataModel {
    actualDateOfInspection?: Date;
    additionalInfo?: string;
    awaitingResponseStatusReason?: string;
    businessUnitId?: number;
    businessUnitName?: string;
    customerId?: number;
    customerName?: string;
    estimatedArrivalDate?: Date;
    europeanNumberOfIdentification?: string;
    friendlyName?: string;
    hasVet: boolean;
    id?: number;
    idPropertyName?: string;
    inspectingCompany?: string;
    inspectionDate?: Date;
    inspector?: string;
    inspectorId?: number;
    rightShipInspectionId: number;
    vesselId: number;
    incidentId: number;
    internationalMaritimeOrganizationNumber?: string;
    isRightShipMember?: boolean;
    pendingSubtask?: boolean;
    portId?: number;
    portName?: string;
    requestedDateOfInspection?: Date;
    reviewer: string;
    reviewerType: string;
    reviewrId: number;
    rightShipInspectionType?: string;
    selected?: boolean;
    status?: string;
    statusDate?: Date;
    statusId: number;
    statusRank: number;
    submittedDate?: Date;
    uri?: string;
    validityDateModified?: boolean;
    vehicleIdentificationNumber?: string;
    vesselName?: string;
    vesselType?: string;
}

export interface InspectorAssignmentODataModel {
    inspectorAssignmentId: number;
    rightShipInspectionId: number;
    inspectorAssignmentFormId? : number;
    companyId?: number;
    freelancerInspectorId?: number;
    freelancerName?: string;
    freelancerEmail?: string;
    freelancerMobileNumber?: string;
    companyName?: string;
    proposalStatusId?: InspectorAssignmentFormProposalStatusEnum;
    proposalStatusName?: string;
    estimatedCost?: number;
    proposalDate?: Date;
    inspectors?: AssignedInspectorModel[]
}

export interface ResendAcceptanceEmailModel {
    rightShipInspectionId: number;
    rightShipInspectionAssignmentId: number;
    technicalManagerEmail: string;
    correspondenceEmail: string;
    assignedInspectors: AssignedInspectorModel[];
    inspectionCompanyEmails: string;
    additionalEmails: string;
}

export interface RightShipInspectionItemSourceModel {
    sourceTableName: InspectorAssignmentStatusEnum;
    itemSource: ItemSource | ((x) => ItemSource);
    initialSorting: DataGridColumn[];
    inspectors: AssignedInspectorModel[];
    initialFilters: QueryFilter[];
}

export interface AssignedInspectorModel {
    inspectorAssignmentId: number;
    name: string;
    email: string;
    mobileNumber: string;
    isTrainee: boolean;
}

export enum FindingRiskLevelEnum
{
    High = 1,
    Medium = 2,
    Low = 3
}

export enum FindingRiskClassificationEnum
{
    Regulatory = 1,
    IndustryStandard = 2
}

export enum InspectorAssignmentFormProposalStatusEnum {
    Pending = 0,
    Submitted = 1,
    Declined = 2,
    InspectorAssigned = 3
}

export enum InspectorAssignmentStatusEnum {
    Active = 1,
    Withdrawn = 2,
    ChangedInspectionDate = 3,
    InspectorUnassigned = 4
}

export interface InspectorResendEmailModel {
    inspectorEmail: string;
    isSelected: boolean;
}

export interface InspectionCompanyResendEmailModel {
    inspectionCompanyEmail: string;
    isSelected: boolean;
}

export interface AdditionalEmailToNotifyModel {
    emailToNotify: string;
    isSelected: boolean;
}

export enum InspectorType {
    Inspector = 1,
    Trainee = 2,
    InspectionCompany = 3,
    Freelancer = 4,
    TechnicalManager = 5,
    Correspondence = 6
}

export interface GetFindingExtractionStatusResponse {
    aiRunId: string;
    isSuccess: boolean;
    processDate: Date;
    isCompleted: boolean;
}

export interface InspectionThumbnail {
    id: string;
    fileName: string;
    page: number;
    src: string;
}

export interface GetRightShipInspectionDocumentLinksResponse {
    documentLinks: RightShipInspectionDocumentLinkItemModel[]
}

export interface RightShipInspectionDocumentLinkItemModel {
    sasUrl: string;
    aiRunId?: string;
    thumbnails: ThumbnailClientModel[]
}

export enum PscInspectionStatusEnum {
    Misreported = 4,
    Closed = 6
}

export interface InspectionOutcomeFindingSummarySection {
    value: number;
    showStopper: boolean;
}

export interface InspectionOutcomeFindingSummaryRiskLevelClassification {
    riskClassification: string;
    riskClassificationId: number;
    count: number;
    sections: InspectionOutcomeFindingSummarySection[]
}

export interface InspectionOutcomeFindingSummaryRiskLevelModel {
    riskLevel: string;
    riskLevelId: number;
    count: number;
    riskClasifications: InspectionOutcomeFindingSummaryRiskLevelClassification[];
}

export interface InspectionOutcomeFindingSummaryResponse {
    riskLevels: InspectionOutcomeFindingSummaryRiskLevelModel[];
    repeatedFindings: string[];
    total: number;
    sections: string[]
}

export interface ComputeInspectionValidityResponse {
    isUnacceptable: boolean;
    validityInMonths: number;
    summary: string;
}

export interface GetAiInsightResponse {
    insightIndicators: InsightIndicatorModel[];
}
